import { notify } from '@affine/component';
import { useI18n } from '@affine/i18n';
import type {
  NavigationArgs,
  WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';
import { validateNavigationArgs } from '@streamlain/copilot-realtime-contract';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import {
  cloneElement,
  createContext,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
  type RefObject,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AffineCommandRegistry } from '../../../commands/registry/registry';
import { mapWorkspaceContextIssueToMessageKey } from './workspace-context-messages';
import {
  navigationActionId,
  NavigationActivationError,
  type UiNavigationDefinition,
} from './workspace-navigation-catalog';
import { WorkspaceViewContextService } from './workspace-view-context';

interface UiNavigationItemBaseProps {
  ownerId: string;
  children: ReactElement;
  geometryRef?: RefObject<HTMLElement | null>;
  /** Search preview rows select on click and open on double click. */
  userActivation?: 'click' | 'double-click';
}
export type UiNavigationItemProps = UiNavigationItemBaseProps &
  (
    | { scope: WorkspaceViewScope; definition: UiNavigationDefinition }
    /**
     * The owner has not offered the definition yet: the child renders as is
     * and stays mounted, so it does not enter again once the definition comes.
     */
    | { scope: WorkspaceViewScope | null; definition: null }
  );
export interface UiNavigationEntryProps {
  scope: WorkspaceViewScope;
  ownerId: string;
  definition: UiNavigationDefinition;
}
const UiNavigationEntryContext = createContext<UiNavigationEntryProps | null>(
  null
);
export function UiNavigationEntryProvider({
  children,
  ...entry
}: UiNavigationEntryProps & { children: ReactNode }) {
  return (
    <UiNavigationEntryContext.Provider value={entry}>
      {children}
    </UiNavigationEntryContext.Provider>
  );
}
export function useUiNavigationEntry() {
  return useContext(UiNavigationEntryContext);
}
/** Both independent UI islands read the same existing command-owned definition. */
export function useUiNavigationDefinition(
  scope: WorkspaceViewScope | null,
  ownerId: string,
  definitionId: string
): UiNavigationDefinition | undefined {
  useLiveData(AffineCommandRegistry.changes$);
  return scope
    ? AffineCommandRegistry.commands.get(
        navigationActionId(scope, ownerId, definitionId)
      )?.navigation?.definition
    : undefined;
}
/** Offers the definitions of one owner while the component is mounted. */
export function useUiNavigationSource(
  scope: WorkspaceViewScope,
  ownerId: string,
  definitions: readonly UiNavigationDefinition[],
  register = true
): void {
  const context = useService(WorkspaceViewContextService);
  const items$ = useMemo(
    () => new LiveData<readonly UiNavigationDefinition[]>([]),
    []
  );
  useLayoutEffect(
    () =>
      register
        ? context.navigation.registerSource({ scope, ownerId, items$ })
        : undefined,
    [context, items$, ownerId, register, scope]
  );
  useLayoutEffect(() => items$.next(definitions), [definitions, items$]);
}

export function UiNavigationItem({
  scope,
  ownerId,
  definition,
  children,
  geometryRef,
  userActivation = 'click',
}: UiNavigationItemProps): ReactElement {
  const context = useService(WorkspaceViewContextService),
    t = useI18n();
  const [element, setElement] = useState<HTMLElement | null>(null),
    slot = useRef<NavigationArgs | null>(null);
  const child = children as ReactElement<
    HTMLAttributes<HTMLElement> & {
      ref?: Ref<HTMLElement>;
      'data-ui-navigation-id'?: string;
    }
  >;
  const ref = useCallback(
    (value: HTMLElement | null) => {
      setElement(value);
      const original = child.props.ref;
      if (typeof original === 'function') return original(value);
      if (original) original.current = value;
    },
    [child.props.ref]
  );
  const target = useMemo(
    () => (scope && definition ? { scope, definition } : null),
    [scope, definition]
  );
  const withClickArgs = useCallback(
    (args: NavigationArgs, click: () => void) => {
      if (!target) throw new NavigationActivationError('blocked');
      const { scope, definition } = target;
      if (slot.current) throw new NavigationActivationError('blocked');
      const validated = validateNavigationArgs(definition.argsSchema, args);
      if (!validated || !definition.available() || !element?.isConnected)
        throw new NavigationActivationError('blocked');
      const current = context.snapshot(scope);
      if (!current.ok)
        throw new NavigationActivationError(
          current.reason === 'stale' ? 'stale' : 'blocked'
        );
      slot.current = validated;
      try {
        click();
      } finally {
        slot.current = null;
      }
    },
    [context, element, target]
  );
  useLayoutEffect(() => {
    if (!element || !target) return;
    const root =
      element.closest<HTMLElement>('[data-workspace-view-id]') ?? element;
    return context.navigation.bind(
      target.scope,
      ownerId,
      target.definition.id,
      root,
      element,
      geometryRef?.current ?? element,
      withClickArgs
    );
  }, [context, element, geometryRef, ownerId, target, withClickArgs]);
  if (!target) return cloneElement(child, { ref });
  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    const { scope, definition } = target;
    if (
      !slot.current &&
      userActivation === 'double-click' &&
      event.type === 'click'
    ) {
      child.props.onClick?.(event);
      return;
    }
    // Preserve the normal link owner's new-tab/new-view behavior for user
    // modifiers: a link opens where the modifier asks, an own handler decides
    // itself. A target with neither activates like on a plain click.
    const ownsModifiers =
      child.props.onClick !== undefined ||
      'to' in child.props ||
      'href' in child.props;
    if (
      !slot.current &&
      ownsModifiers &&
      (event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.shiftKey ||
        event.button !== 0)
    ) {
      child.props.onClick?.(event);
      return;
    }
    event.preventDefault();
    const issue = (error: unknown) =>
      notify.error({
        title:
          t[
            mapWorkspaceContextIssueToMessageKey(
              error instanceof NavigationActivationError
                ? error.reason
                : 'blocked'
            )
          ](),
      });
    try {
      if (!context.isActiveScope(scope))
        throw new NavigationActivationError('stale');
      const args = validateNavigationArgs(
        definition.argsSchema,
        slot.current ?? {}
      );
      if (!args || !definition.available())
        throw new NavigationActivationError('blocked');
      if (!slot.current && definition.reselect?.()) return;
      Promise.resolve(definition.activate(args)).catch(issue);
    } catch (error) {
      issue(error);
    }
  };
  return cloneElement(child, {
    ref,
    onClick,
    ...(userActivation === 'double-click' ? { onDoubleClick: onClick } : {}),
    'data-ui-navigation-id': navigationActionId(
      target.scope,
      ownerId,
      target.definition.id
    ),
    'aria-label': target.definition.label,
  });
}
export interface UiNavigationSectionProps {
  /** A containing normal UI owner can already publish this definition. */
  registerSource?: boolean;
  headingProps?: HTMLAttributes<HTMLHeadingElement>;
  sectionProps?: HTMLAttributes<HTMLElement>;
  headingContent?: ReactNode;
  buttonProps?: HTMLAttributes<HTMLButtonElement>;
  scope: WorkspaceViewScope;
  ownerId: string;
  definition: UiNavigationDefinition;
  children?: ReactNode;
}
export function UiNavigationSection({
  registerSource = true,
  sectionProps,
  headingProps,
  headingContent,
  buttonProps,
  scope,
  ownerId,
  definition,
  children,
}: UiNavigationSectionProps): ReactElement {
  const definitions = useMemo(() => [definition], [definition]);
  useUiNavigationSource(scope, ownerId, definitions, registerSource);
  return (
    <section
      {...sectionProps}
      data-ui-navigation-id={navigationActionId(scope, ownerId, definition.id)}
      aria-label={definition.label}
    >
      <h2 {...headingProps}>
        <UiNavigationItem
          scope={scope}
          ownerId={ownerId}
          definition={definition}
        >
          <button {...buttonProps} type="button">
            {headingContent ?? definition.label}
          </button>
        </UiNavigationItem>
      </h2>
      {children}
    </section>
  );
}
